import React, { useState, useEffect, useRef } from 'react'
import axios from 'axios'
import PropTypes from 'prop-types'
import { AlertTriangle, X } from 'lucide-react'
import { useForm, Controller } from 'react-hook-form'
import { Drawer, DrawerContent, DrawerHeader, DrawerTitle, DrawerFooter, DrawerClose } from '../../ui/drawer'
import { Button } from '../../ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../ui/card'
import { Input } from '../../ui/input'
import { Textarea } from '../../ui/textarea'
import { Label } from '../../ui/label'
import { Checkbox } from '../../ui/checkbox'
import { Switch } from '../../ui/switch'
import LoadingSpinner from '../../leevo_ui/LoadingSpinner/LoadingSpinner'
import { requestCreator } from '../../../utilities/requests'
import { mapKlassPackagePayload } from '../../../utilities/apiPayloadMappers'
import CheckButton from '../../leevo_ui/CheckButton/CheckButton'
import Avatar from '../../leevo_ui/Avatar/Avatar'
import MultiSelectStudent from '../../leevo_ui/KlassForm/MultiSelectStudent/MultiSelectStudent'
import CurrencyInput from '../../leevo_ui/CurrencyInput/CurrencyInput'
import { extractErrorMessage } from '../../../utilities/errorHelpers'

const { get: getKlassPackage, put: updateKlassPackage, cancel: cancelKlassPackageRequests } = requestCreator()
const { get: getKlasses, cancel: cancelKlassesRequests } = requestCreator()
const { get: getStudents, cancel: cancelStudentsRequests } = requestCreator()

const klassFields = 'dayOfWeek,timeOfDay,startDate,endDate,remainingKlassDays,coaches,supportedSkillLevels'
const coachFields = 'fullName,preferredName,email,cloudinaryPhotoPublicId'
const studentFields = 'fullName,preferredName,currentSkillLevel,age'

function ClassPackageEditDrawer({ open, onOpenChange, klassPackageId, facilityId, onSuccess }) {
  const [isLoading, setIsLoading] = useState(true)
  const [isSaving, setIsSaving] = useState(false)
  const [loadError, setLoadError] = useState('')
  const loadVersion = useRef(0)
  const [klassPackageData, setKlassPackageData] = useState(null)
  const [availableKlasses, setAvailableKlasses] = useState([])

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors }
  } = useForm({
    defaultValues: {
      title: '',
      description: '',
      price: null,
      active: true,
      proratedPricing: true,
      klasses: [],
      students: []
    }
  })

  useEffect(() => {
    if (open && klassPackageId) {
      loadKlassPackageData()
    }

    return () => {
      loadVersion.current += 1
      cancelKlassPackageRequests()
      cancelKlassesRequests()
      cancelStudentsRequests()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, klassPackageId, facilityId])

  async function loadKlassPackageData() {
    const version = ++loadVersion.current
    setIsLoading(true)
    setLoadError('')
    setKlassPackageData(null)
    try {
      const [klassPackage, klasses, studentsResponse] = await Promise.all([
        getKlassPackage(`/api/facilities/${facilityId}/class_packages/${klassPackageId}`, {
          params: {
            include: 'students,students.current_skill_level,klasses,klasses.coaches,klasses.supported_skill_levels',
            fields: {
              klass_package: 'title,description,price,active,proratedPricing,students,klasses',
              klass: klassFields,
              coach: coachFields,
              student: studentFields,
              skill_level: 'name'
            }
          }
        }),
        getKlasses(`/api/facilities/${facilityId}/klasses`, {
          params: {
            include: 'coaches,supported_skill_levels',
            fields: { klass: klassFields, coach: coachFields, skill_level: 'name' }
          }
        }),
        getStudents(`/api/facilities/${facilityId}/students`, {
          params: {
            include: 'current_skill_level',
            fields: {
              student: studentFields,
              skill_level: 'name'
            }
          }
        })
      ])

      if (version !== loadVersion.current) return
      setKlassPackageData(klassPackage)

      const selectedKlassIds = new Set(klassPackage.klasses?.map((klass) => klass.id) || [])
      const klassesById = new Map([...(klassPackage.klasses || []), ...klasses].map((klass) => [klass.id, klass]))
      const formattedKlasses = Array.from(klassesById.values()).map((klass) => ({
        ...klass,
        selected: selectedKlassIds.has(klass.id)
      }))
      setAvailableKlasses(formattedKlasses)

      const selectedStudentIds = new Set((klassPackage.students || []).map((student) => student.id))
      const studentsById = new Map(
        [...(klassPackage.students || []), ...studentsResponse.records].map((student) => [student.id, student])
      )
      const formattedStudents = Array.from(studentsById.values()).map((student) => ({
        ...student,
        value: student.id,
        selected: selectedStudentIds.has(student.id)
      }))

      reset({
        title: klassPackage.title || '',
        description: klassPackage.description || '',
        price: klassPackage.price ?? null,
        active: klassPackage.active !== undefined ? klassPackage.active : true,
        proratedPricing: klassPackage.proratedPricing !== undefined ? klassPackage.proratedPricing : true,
        klasses: klassPackage.klasses?.map((k) => k.id) || [],
        students: formattedStudents
      })
    } catch (error) {
      if (version === loadVersion.current && !axios.isCancel(error)) {
        setLoadError(
          error.response?.status === 401
            ? 'Your session has expired. Sign in again to edit this package.'
            : extractErrorMessage(error)
        )
      }
    } finally {
      if (version === loadVersion.current) setIsLoading(false)
    }
  }

  async function onSubmit(data) {
    if (isLoading || loadError || !klassPackageData || String(klassPackageData.id) !== String(klassPackageId)) return
    setIsSaving(true)
    try {
      await updateKlassPackage(`/api/facilities/${facilityId}/class_packages/${klassPackageId}`, {
        klassPackage: mapKlassPackagePayload({
          title: data.title,
          description: data.description,
          price: data.price,
          active: data.active,
          proratedPricing: data.proratedPricing,
          klasses: data.klasses,
          students: data.students.filter((s) => s.selected).map((s) => s.id)
        })
      })

      onSuccess?.('Class package updated successfully!')
      onOpenChange(false)
    } catch (error) {
      alert(extractErrorMessage(error))
    } finally {
      setIsSaving(false)
    }
  }

  const drawerTitle = klassPackageData ? klassPackageData.title : 'Edit Class Package'

  return (
    <>
      <Drawer open={open} onOpenChange={onOpenChange}>
        <DrawerContent className="flex flex-col md:!w-[800px] md:!min-w-[800px] md:!max-w-[800px]">
          <DrawerHeader className="border-b border-gray-200 flex-shrink-0">
            <div className="flex items-center justify-between">
              <DrawerTitle>{drawerTitle}</DrawerTitle>
              <DrawerClose asChild>
                <Button variant="ghost" size="sm">
                  <X className="w-4 h-4" />
                </Button>
              </DrawerClose>
            </div>
          </DrawerHeader>

          <div className="flex-1 overflow-y-auto overflow-x-hidden">
            {isLoading ? (
              <div className="flex items-center justify-center h-64 w-full min-w-[700px]">
                <LoadingSpinner />
              </div>
            ) : loadError ? (
              <div role="alert" className="p-6 space-y-4">
                <p>{loadError}</p>
                <Button onClick={loadKlassPackageData}>Retry</Button>
              </div>
            ) : (
              <form onSubmit={handleSubmit(onSubmit)} className="p-4 space-y-4">
                <Card>
                  <CardHeader>
                    <CardTitle>Menu Info</CardTitle>
                    <CardDescription>
                      This information is displayed to parents and guardians when they are picking items from your menu
                      of classes.
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-6">
                    <div className="space-y-2">
                      <Label htmlFor="title">
                        Title <span className="text-red-500">*</span>
                      </Label>
                      <Controller
                        name="title"
                        control={control}
                        rules={{ required: 'Title is required' }}
                        render={({ field }) => (
                          <Input
                            {...field}
                            id="title"
                            type="text"
                            placeholder="Monday & Wednesday Package"
                            className={errors.title ? 'border-red-500 focus-visible:ring-red-500' : ''}
                          />
                        )}
                      />
                      <p className="text-xs text-gray-500">This is displayed in large bold print on the menu.</p>
                      {errors.title && <p className="text-xs text-red-500">{errors.title.message}</p>}
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="description">Description</Label>
                      <Controller
                        name="description"
                        control={control}
                        render={({ field }) => (
                          <Textarea
                            {...field}
                            id="description"
                            maxLength={200}
                            placeholder="Classes run Monday and Wednesday evenings from 6:00-7:00 PM"
                            rows={3}
                          />
                        )}
                      />
                      <p className="text-xs text-gray-500">
                        This is displayed below the title on the menu. Maximum 200 characters.
                      </p>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="price">
                        Price <span className="text-red-500">*</span>
                      </Label>
                      <Controller
                        name="price"
                        control={control}
                        rules={{ required: 'Price is required', min: { value: 0, message: 'Price must be positive' } }}
                        render={({ field }) => (
                          <CurrencyInput
                            id="price"
                            placeholder="$120.00"
                            value={field.value}
                            onChange={field.onChange}
                            onBlur={field.onBlur}
                            errorMessage={errors.price?.message}
                          />
                        )}
                      />
                      <p className="text-xs text-gray-500">
                        This is the base price of the package. On the menu, this price is pro-rated based on how many
                        classes are left.
                      </p>
                    </div>

                    <div className="flex items-center space-x-2">
                      <Controller
                        name="active"
                        control={control}
                        render={({ field }) => (
                          <Checkbox
                            id="active"
                            checked={field.value}
                            onCheckedChange={field.onChange}
                            data-cy="active-checkbox-input"
                          />
                        )}
                      />
                      <Label htmlFor="active" className="cursor-pointer">
                        Active on menu
                      </Label>
                    </div>

                    <div className="space-y-2">
                      <div className="flex items-center justify-between">
                        <div className="space-y-0.5">
                          <Label htmlFor="proratedPricing">Enable Prorated Pricing</Label>
                          <p className="text-xs text-gray-500">
                            When enabled, the price will be automatically adjusted based on how many classes remain in
                            the package.
                          </p>
                        </div>
                        <Controller
                          name="proratedPricing"
                          control={control}
                          render={({ field }) => (
                            <Switch
                              id="proratedPricing"
                              checked={field.value}
                              onCheckedChange={field.onChange}
                              data-cy="prorated-pricing-switch"
                            />
                          )}
                        />
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CardTitle>Classes</CardTitle>
                    <CardDescription>
                      <div className="space-y-2">
                        <p>When a student buys this package, we auto-place them in these classes.</p>
                        <p>Leevo will only add a student to one class per day for their level.</p>
                        <p>
                          If no class supports their level, they will not be able to see this package during
                          registration.
                        </p>
                      </div>
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <Controller
                      name="klasses"
                      control={control}
                      render={({ field }) => {
                        const selectedIds = new Set(field.value)
                        const upcomingKlasses = availableKlasses.filter((klass) => klass.remainingKlassDays > 0)
                        const completedKlasses = availableKlasses.filter(
                          (klass) => selectedIds.has(klass.id) && klass.remainingKlassDays === 0
                        )

                        return (
                          <div className="space-y-4">
                            {completedKlasses.length > 0 && (
                              <div className="overflow-hidden border border-amber-300 rounded-md bg-amber-50">
                                <div className="flex gap-3 p-4 text-amber-900">
                                  <AlertTriangle className="flex-shrink-0 w-5 h-5 mt-0.5" aria-hidden="true" />
                                  <div>
                                    <p className="text-sm font-semibold">Remove completed classes</p>
                                    <p className="mt-1 text-xs">
                                      Active packages cannot combine completed classes with current or upcoming classes.
                                    </p>
                                  </div>
                                </div>
                                <ul className="m-0 border-t border-amber-200">
                                  {completedKlasses.map((klass) => (
                                    <li
                                      key={klass.id}
                                      className="flex items-center justify-between gap-4 px-4 py-3 border-b border-amber-200 last:border-b-0"
                                    >
                                      <div>
                                        <p className="text-sm font-medium text-gray-900">
                                          {klass.dayOfWeek} - {klass.timeOfDay}
                                        </p>
                                        <p className="text-xs text-gray-600">
                                          {klass.startDate} to {klass.endDate}
                                        </p>
                                      </div>
                                      <Button
                                        type="button"
                                        variant="outline"
                                        size="sm"
                                        onClick={() =>
                                          field.onChange(field.value.filter((klassId) => klassId !== klass.id))
                                        }
                                      >
                                        Remove
                                      </Button>
                                    </li>
                                  ))}
                                </ul>
                              </div>
                            )}

                            <div className="space-y-2">
                              <Label>Select Classes</Label>
                              <div className="h-64 overflow-y-auto border border-gray-300 rounded-md">
                                {upcomingKlasses.length === 0 && (
                                  <div className="flex flex-col justify-center m-12 font-light text-center">
                                    <p>You don&apos;t have any upcoming classes.</p>
                                    <p data-cy="create-class-link">
                                      <a
                                        className="font-normal text-purple-600 hover:text-purple-400"
                                        href={`/facilities/${facilityId}/classes/new`}
                                      >
                                        Create some classes
                                      </a>{' '}
                                      with dates in the future to see them appear in this list.
                                    </p>
                                  </div>
                                )}
                                <ul className="m-0">
                                  {upcomingKlasses.map((klass) => (
                                    <li
                                      key={klass.id}
                                      className="flex items-start w-full px-4 py-2 border-b border-gray-300 cursor-pointer hover:bg-gray-50"
                                      onClick={() => {
                                        const valueAsSet = new Set(field.value)
                                        if (valueAsSet.has(klass.id)) {
                                          valueAsSet.delete(klass.id)
                                        } else {
                                          valueAsSet.add(klass.id)
                                        }
                                        field.onChange(Array.from(valueAsSet))
                                      }}
                                    >
                                      <div className="w-1/12">
                                        <CheckButton
                                          className="mt-1 mr-4"
                                          ariaProps={{
                                            'aria-pressed': selectedIds.has(klass.id),
                                            'aria-label': 'select class'
                                          }}
                                          checked={selectedIds.has(klass.id)}
                                          data-cy={`klass-${klass.id}-check-button`}
                                          size={5}
                                        />
                                      </div>
                                      <div className="w-11/12">
                                        <header className="flex items-center justify-between">
                                          <span className="inline-block text-sm font-medium xs:text-base">
                                            {klass.dayOfWeek} - {klass.timeOfDay}
                                          </span>
                                          <span className="inline-block text-xs font-medium xs:text-sm">
                                            {klass.startDate} to {klass.endDate}
                                          </span>
                                        </header>
                                        <div className="flex justify-between">
                                          <div className="w-full">
                                            <div className="flex flex-wrap items-center mt-1">
                                              {klass.coaches.map((coach) => (
                                                <div key={coach.id} className="flex items-center mr-2 last:mr-0">
                                                  <Avatar
                                                    fullName={coach.fullName}
                                                    cloudinaryPhotoPublicId={coach.cloudinaryPhotoPublicId}
                                                    size={5}
                                                    userId={coach.id}
                                                  />
                                                  <span className="ml-1 text-xs font-medium text-gray-600">
                                                    {coach.preferredName || coach.email}
                                                  </span>
                                                </div>
                                              ))}
                                            </div>
                                            <span className="inline-block w-full mt-1 text-xs font-medium text-gray-600 truncate">
                                              {klass.supportedSkillLevels
                                                .map((skillLevel) => skillLevel.name)
                                                .join(', ')}
                                            </span>
                                          </div>
                                        </div>
                                      </div>
                                    </li>
                                  ))}
                                </ul>
                              </div>
                            </div>
                          </div>
                        )
                      }}
                    />
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CardTitle>Students</CardTitle>
                    <CardDescription>
                      <div className="space-y-2">
                        <p>Students are automatically added to a package when registering.</p>
                        <p>Add a student to enroll them in all selected classes.</p>
                        <p>Remove a student to un-enroll them from all selected classes.</p>
                      </div>
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <Controller
                      name="students"
                      control={control}
                      render={({ field }) => {
                        const fieldProps = { ...field }
                        delete fieldProps.ref
                        return <MultiSelectStudent {...fieldProps} onChange={field.onChange} loading={false} />
                      }}
                    />
                  </CardContent>
                </Card>
              </form>
            )}
          </div>

          <DrawerFooter className="border-t border-gray-200 flex-shrink-0">
            <div className="flex w-full space-x-2">
              <Button
                onClick={handleSubmit(onSubmit)}
                disabled={isSaving || isLoading || !!loadError || !klassPackageData}
                className="flex-1"
              >
                {isSaving ? 'Saving...' : 'Save Changes'}
              </Button>
              <DrawerClose asChild>
                <Button variant="outline" className="flex-1">
                  Cancel
                </Button>
              </DrawerClose>
            </div>
          </DrawerFooter>
        </DrawerContent>
      </Drawer>
    </>
  )
}

ClassPackageEditDrawer.propTypes = {
  open: PropTypes.bool.isRequired,
  onOpenChange: PropTypes.func.isRequired,
  klassPackageId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  facilityId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  onSuccess: PropTypes.func
}

export default ClassPackageEditDrawer
